/** @prettier */

import {Grid, Stack, Box, Container} from '@mui/material';
import {useState, useEffect} from 'react';
import {useLoaderData, useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import EnhancedTable from './DataTable';
import {PrimaryButton} from '../../component/BasicButton';
//API import
import {
  postReportStates,
  getReportStates,
  getCompositionsSitesBySiteID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {limit} from '../../define';
import {
  convertByteIntoKiloByte,
  convertByteIntoMegaByte,
} from '../../functions/convertNumber';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';
import {checkSiteInfoBillingStatus} from '../../functions/checkBillingStatus';

type LoaderReturnType = {
  timeZone: string; //タイムゾーン
  reportList: apiIF.res.getReportStates; //日報一覧
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  const promises = [];
  //サイトの情報を取得
  promises.push(getCompositionsSitesBySiteID(params.siteID));
  //日報の一覧を取得
  promises.push(getReportStates(params.siteID));
  //熱源機の登録状況を取得
  promises.push(checkUnitExistence(params.siteID));

  try {
    const response = await Promise.all(promises);
    const siteInfo = response[0] as apiIF.res.getCompositionsSitesBySiteID;
    const reportInfo = response[1] as apiIF.res.getReportStates;
    const heatSourceUnit = response[2] as unitExistence;

    //全ての熱源機ユニットが未登録ならリダイレクト
    const unregistered = Object.values(heatSourceUnit).every(value => {
      return value === false;
    });
    if (unregistered) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    //全GWに課金情報がなければリダイレクト
    if (checkSiteInfoBillingStatus(siteInfo['billing-status']) === false) {
      throw new Error('Billing status does not exist');
    }

    return {
      timeZone: siteInfo.timezone,
      reportList: reportInfo,
    };
  } catch {
    return undefined;
  }
}

/**
 * 日報一覧
 * @returns JSX
 */
const DailyReport = () => {
  const trans = useTranslation().t; //翻訳関数
  const siteInfo = useLoaderData() as LoaderReturnType | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();

  const [dailyDownloadList, setDailyDate] = useState<readonly string[]>([]); //ダウンロードする日報
  const [dailySize, setDailySize] = useState(0); //ダウンロードする日報のサイズ
  const [monthlyDownloadList, setMonthlyDate] = useState<readonly string[]>([]); //ダウンロードする月報
  const [monthlySize, setMonthlySize] = useState(0); //ダウンロードする月報のサイズ
  const [otherDownloadList, setOtherDate] = useState<readonly string[]>([]); //ダウンロードするその他の日報
  const [otherSize, setOtherSize] = useState(0); //ダウンロードするその他の日報のサイズ
  const [downloadBtnDisabled, setDownloadBtnDisabled] = useState(true); //Downloadボタン非表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //Lambdaが1回に処理できる上限が512MBなので制限を設ける
  useEffect(() => {
    if (
      convertByteIntoMegaByte(dailySize + monthlySize + otherSize, 1) >
      limit.DOWNLOAD_FILE_SIZE_MAX
    ) {
      setDownloadBtnDisabled(true);
    } else if (dailySize + monthlySize + otherSize === 0) {
      setDownloadBtnDisabled(true);
    } else {
      setDownloadBtnDisabled(false);
    }
  }, [dailySize, monthlySize, otherSize, siteInfo]);

  /**
   * Downloadボタン押下時処理
   * @returns
   */
  const downloadReport = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    setAPIWait(true);
    setDownloadBtnDisabled(true);

    //日報データダウンロード
    let downloadList: apiIF.reqParameters.postReportStates = {};
    if (dailyDownloadList.length > 0) {
      downloadList['daily-reports'] = [...dailyDownloadList];
    }
    if (monthlyDownloadList.length > 0) {
      downloadList['monthly-reports'] = [...monthlyDownloadList];
    }
    if (otherDownloadList.length > 0) {
      downloadList['other-reports'] = [...otherDownloadList];
    }
    postReportStates(siteID, downloadList)
      .then(response => {
        const link = document.createElement('a');
        link.href = response['download-path'];
        link.click();
        setAPIWait(false);
        setDownloadBtnDisabled(false);
      })
      .catch(err => {
        setAPIWait(false);
        setDownloadBtnDisabled(false);
      });
  };

  //タイムゾーン、日報一覧を取得できない場合
  if (siteInfo === undefined) {
    return <></>;
  }
  return (
    <Container fixed>
      <Grid container rowSpacing={5}>
        {/** タイトル、説明 */}
        <Grid item xs={12}>
          <h1>{trans('DailyReport.title')}</h1>
          <h2 className="serviceInfo__dailyReportData__instruction">
            {trans('DailyReport.instruction')}
          </h2>
        </Grid>
        {/** レポート一覧 */}
        <Grid item xs={12} container justifyContent="center" spacing={3}>
          <Grid item xs={12} lg={6}>
            <Stack>
              <h3>{trans('DailyReport.heading.daily')}</h3>
              <EnhancedTable
                tableData={siteInfo.reportList['daily-reports']}
                tableClassName="serviceInfo__dailyReportData__report--daily"
                setRetData={setDailyDate}
                setSize={setDailySize}
                timeZone={siteInfo.timeZone}
                id="dailyData"
                orderBy="fileName"
              />
            </Stack>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Stack>
              <h3>{trans('DailyReport.heading.monthly')}</h3>
              <EnhancedTable
                tableData={siteInfo.reportList['monthly-reports']}
                tableClassName="serviceInfo__dailyReportData__report--monthly"
                setRetData={setMonthlyDate}
                setSize={setMonthlySize}
                timeZone={siteInfo.timeZone}
                id="monthlyData"
                orderBy="fileName"
              />
              <h3>{trans('DailyReport.heading.other')}</h3>
              <EnhancedTable
                tableData={siteInfo.reportList['other-reports']}
                tableClassName="serviceInfo__dailyReportData__report--other"
                setRetData={setOtherDate}
                setSize={setOtherSize}
                timeZone={siteInfo.timeZone}
                id="otherData"
                orderBy="fileName"
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid item xs={12} container justifyContent="flex-end">
          <Grid item xs="auto">
            <Stack spacing={1}>
              <PrimaryButton
                onClick={downloadReport}
                buttonText={trans('DailyReport.buttonTxt.download')}
                id="mv-reportDataDownloadBtn"
                disabled={downloadBtnDisabled}
                className="serviceInfo__dailyReportData__button"
                apiWait={apiWait}
              />
              <Box>
                {trans('DailyReport.totalSize', {
                  currentSize: convertByteIntoKiloByte(
                    dailySize + monthlySize + otherSize,
                    1
                  ),
                  maximumSize: limit.DOWNLOAD_FILE_SIZE_MAX,
                })}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};
export default DailyReport;
